import React, { useState, useEffect } from "react";
import "../styles/Cart.css";
import { loadStripe } from "@stripe/stripe-js";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";

function VideoPlayer({ videos }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setCurrentIndex(0);
  }, [videos]);

  const handleEnded = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex + 1 < videos.length ? prevIndex + 1 : 0
    );
  };

  if (!videos || videos.length === 0) return null;

  return (
    <video
      src={videos[currentIndex]}
      className="product-image"
      controls
      muted
      playsInline
      preload="metadata"
      autoPlay
      onEnded={handleEnded}
    />
  );
}

function Cart({ isOpen, onClose, centered }) {
  const [products, setProducts] = useState([]);
  const [shippingInfo, setShippingInfo] = useState({
    name: "",
    address: "",
    postalCode: "",
    city: "",
    country: "",
    email: "",
    phone: "",
  });
  const [codData, setCodData] = useState({
    phone: "",
    idNumber: "",
    notes: "",
  });

  useEffect(() => {
    const storedCart = localStorage.getItem("cart");
    if (storedCart) {
      const parsedProducts = JSON.parse(storedCart).map((p) => ({
        ...p,
        quantity: p.quantity || 1,
      }));
      setProducts(parsedProducts);
    }
  }, []);

  const stripePromise = loadStripe(
    "pk_live_51NkRiEBeI4d7ilSKlBWh9Oh6amrY7Mn0s2hSoJkhYy57eoED2cVFV7iXgPULTMVbGXD3yhSBljk7H7tIHEKVrysL004o7QMKdF"
  );

  // Elimina un producto del carrito según el índice
  const handleRemove = (index) => {
    const newProducts = products.filter((_, i) => i !== index);
    setProducts(newProducts);
    localStorage.setItem("cart", JSON.stringify(newProducts));
    window.dispatchEvent(new Event("storage"));
  };

  // Cambia la cantidad de un producto, mínimo 1
  const handleQuantityChange = (index, value) => {
    let qty = parseInt(value, 10);
    if (isNaN(qty) || qty < 1) qty = 1;
    const newProducts = products.map((p, i) =>
      i === index ? { ...p, quantity: qty } : p
    );
    setProducts(newProducts);
    localStorage.setItem("cart", JSON.stringify(newProducts));
    window.dispatchEvent(new Event("storage"));
  };

  if (!isOpen) return null;

  const saveOrder = async (order) => {
    try {
      await addDoc(collection(db, "orders"), order);
      console.log("Pedido guardado con éxito en Firestore");
    } catch (err) {
      console.error("Error guardando el pedido:", err);
    }
  };

  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setShippingInfo({ ...shippingInfo, [name]: value });
  };

  const handleCodChange = (e) => {
    const { name, value } = e.target;
    setCodData({ ...codData, [name]: value });
  };

  // Dentro de startCardPayment:
  const startCardPayment = () => {
    (async () => {
      if (products.length === 0) return alert("El carrito está vacío.");
  
      const stripeProducts = products.filter((p) => p.priceId);
      if (!stripeProducts.length)
        return alert("No hay productos válidos para Stripe.");
  
      try {
        const WORKER_URL = "https://patient-rain-0155.dev-bd1.workers.dev";
        const res = await fetch(WORKER_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ products: stripeProducts, shippingInfo }),
          mode: "cors",
        });
        const data = await res.json();
  
        if (res.ok && data.sessionId) {
          // Vaciar carrito local
          localStorage.removeItem("cart");
          setProducts([]);
          window.dispatchEvent(new Event("storage"));
  
          const stripe = await stripePromise;
          await stripe.redirectToCheckout({ sessionId: data.sessionId });
        } else {
          console.error("No se generó sessionId:", data);
        }
      } catch (err) {
        console.error("Error iniciando Stripe Checkout:", err);
      }
    })();
  };

  const finishCodOrder = async () => {
    if (products.length === 0) return alert("El carrito está vacío.");
    if (!shippingInfo.name || !shippingInfo.address || !shippingInfo.email)
      return alert("Rellena al menos nombre, dirección y correo electrónico.");
    if (!codData.phone || codData.phone.length < 6)
      return alert("Introduce un teléfono válido para contrareembolso.");
    if (!codData.idNumber || codData.idNumber.length < 4)
      return alert(
        "Introduce un identificador (DNI/ID) válido para contrareembolso."
      );

    const order = {
      products,
      shippingInfo,
      total: products.reduce((sum, p) => sum + p.price * p.quantity, 0),
      paymentMethod: "contrareembolso",
      codInfo: codData,
      createdAt: new Date().toISOString(),
      status: "pendiente_con_cod",
    };
    await saveOrder(order);

    localStorage.removeItem("cart");
    setProducts([]);
    window.dispatchEvent(new Event("storage"));
    alert(
      "Pedido registrado. Te contactaremos para confirmar el contrareembolso."
    );
    onClose();
  };

  return (
    <div
      className="cart-modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.6)",
        zIndex: 10000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        className="cart-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "50%",
          maxHeight: "90vh",
          overflowY: "auto",
          borderRadius: "12px",
          backgroundColor: "#fff",
          zIndex: 9999,
        }}
      >
        <button
          className="cart-close"
          onClick={onClose}
          aria-label="Cerrar carrito"
        >
          ✕
        </button>
        <section className="cart">
          <h2>Carrito</h2>
          {products.length === 0 ? (
            <p className="empty">Tu carrito está vacío.</p>
          ) : (
            <>
              <ul>
                {products.map((product, index) => (
                  <li key={index}>
                    {product.video ? (
                      <VideoPlayer videos={Array.isArray(product.video) ? product.video : [product.video]} />
                    ) : (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="product-image"
                      />
                    )}
                    <div>
                      <p>{product.name}</p>
                      <p>
                        €{((product.price * product.quantity) / 100).toFixed(2)}
                      </p>
                    </div>
                    <div className="finalizacion">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={product.quantity}
                        onChange={(e) =>
                          handleQuantityChange(index, e.target.value)
                        }
                        className="quantity-input"
                      />
                      <button onClick={() => handleRemove(index)}>
                        Eliminar
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="total">
                Total: €
                {(
                  products.reduce((sum, p) => sum + p.price * p.quantity, 0) /
                  100
                ).toFixed(2)}
              </p>

              <div className="shipping-form">
                <h3>Dirección de envío</h3>
                <input
                  type="text"
                  name="name"
                  placeholder="Nombre completo"
                  value={shippingInfo.name}
                  onChange={handleShippingChange}
                  required
                />
                <input
                  type="text"
                  name="address"
                  placeholder="Dirección"
                  value={shippingInfo.address}
                  onChange={handleShippingChange}
                  required
                />
                <input
                  type="text"
                  name="postalCode"
                  placeholder="Código Postal"
                  value={shippingInfo.postalCode}
                  onChange={handleShippingChange}
                  required
                />
                <input
                  type="text"
                  name="city"
                  placeholder="Ciudad"
                  value={shippingInfo.city}
                  onChange={handleShippingChange}
                  required
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Correo electrónico"
                  value={shippingInfo.email}
                  onChange={handleShippingChange}
                  required
                />
                <input
                  type="text"
                  name="phone"
                  placeholder="Teléfono"
                  value={shippingInfo.phone}
                  onChange={handleShippingChange}
                  required
                />
              </div>

              <div className="payment-methods" style={{ marginTop: 16 }}>
                <h3>Métodos de pago</h3>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={startCardPayment}
                    disabled={
                      !shippingInfo.name ||
                      !shippingInfo.address ||
                      !shippingInfo.postalCode ||
                      !shippingInfo.city ||
                      !shippingInfo.email ||
                      !shippingInfo.phone
                    }
                    style={{
                      padding: "8px 12px",
                      borderRadius: 8,
                      cursor: "pointer",
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      flexGrow: 1,
                      justifyContent: "center",
                      opacity:
                        !shippingInfo.name ||
                        !shippingInfo.address ||
                        !shippingInfo.postalCode ||
                        !shippingInfo.city ||
                        !shippingInfo.email ||
                        !shippingInfo.phone
                          ? 0.5
                          : 1,
                      pointerEvents:
                        !shippingInfo.name ||
                        !shippingInfo.address ||
                        !shippingInfo.postalCode ||
                        !shippingInfo.city ||
                        !shippingInfo.email ||
                        !shippingInfo.phone
                          ? "none"
                          : "auto",
                    }}
                  >
                    <span style={{ fontSize: 18 }}>💳</span>
                    <span>Tarjeta</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default Cart;
